"use client";

import React, { useMemo } from "react";
import { ArrowUp, Equal, GitCompare, X } from "lucide-react";

import type { HierarchyTreeNode } from "../../services/agency-hierarchy";

/**
 * Side-by-side comparison of two branches of the org chart.
 *
 * Owners do this in a spreadsheet today: export the tree, paste two rows next to each
 * other, eyeball which branch is carrying the book. This renders that comparison directly
 * off the tree the chart already has, so there is nothing to export and nothing to keep
 * in sync.
 *
 * PERSONAL vs HIERARCHY is the whole point of the table. A branch head can look weak on
 * their own production while their downline carries a third of the org — reading only one
 * of those numbers produces the wrong conclusion, so every money metric is shown at both
 * scopes and the per-agent efficiency figure is derived from the hierarchy scope.
 */

/** Matches the chart's own formatter (AgencyHierarchyTab) so the two surfaces agree. */
const money = (n: number) => {
  if (!Number.isFinite(n)) return "$0";
  const v = Math.round(n);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(Math.abs(v) >= 10_000 ? 0 : 1)}k`;
  return `$${v}`;
};

const countFmt = new Intl.NumberFormat("en-US");
const count = (n: number) => (Number.isFinite(n) ? countFmt.format(Math.round(n)) : "0");

type MetricKind = "money" | "count";

interface Metric {
  key: string;
  label: string;
  /** Short "why this row exists" line under the label. */
  hint?: string;
  kind: MetricKind;
  a: number;
  b: number;
  /** Rows that open a new scope get a divider above them. */
  group?: string;
}

/**
 * Safe ratio. Agencies with zero agents are common (a shell created for a recruit who
 * never onboarded), and a branch with no production at all is the exact case an owner
 * opens this panel to find — so every quotient in this file goes through here.
 */
const ratio = (numerator: number, denominator: number): number =>
  denominator > 0 ? numerator / denominator : 0;

/**
 * Relative gap between the two sides, expressed against the SMALLER value.
 *
 * "$40k vs $10k" reads as "+300%" rather than "+75%" — the multiple is what an owner
 * actually reasons about when deciding where to put attention. Returns null when the
 * smaller side is 0, because "infinitely better" is not a number worth printing.
 */
const relativeGap = (a: number, b: number): number | null => {
  const lo = Math.min(a, b);
  const hi = Math.max(a, b);
  if (lo <= 0) return null;
  return ((hi - lo) / lo) * 100;
};

const pctLabel = (pct: number | null): string | null => {
  if (pct === null) return null;
  if (pct >= 1000) return `+${Math.round(pct / 100) / 10}x`;
  return `+${pct >= 10 ? Math.round(pct) : pct.toFixed(1)}%`;
};

function MetricRow({
  metric,
  accent,
}: {
  metric: Metric;
  accent: string;
}) {
  const { a, b, kind } = metric;
  const fmt = kind === "money" ? money : count;

  // A tie is a real answer. Floating-point money sums can land a cent apart on values
  // that are conceptually identical, so ties are decided on the RENDERED figure — if the
  // two cells print the same string, no side "wins".
  const aText = fmt(a);
  const bText = fmt(b);
  const tie = aText === bText;
  const aLeads = !tie && a > b;
  const bLeads = !tie && b > a;

  const absDelta = Math.abs(a - b);
  const pct = pctLabel(relativeGap(a, b));

  const winnerCell: React.CSSProperties = {
    color: accent,
    // color-mix keeps the tint valid whatever colour space the tenant's brand colour
    // arrives in — `accent` may be an oklch var, a hex, or an rgb string.
    backgroundColor: `color-mix(in oklab, ${accent} 10%, transparent)`,
  };

  const cell = (leads: boolean, text: string) => (
    <td
      className={`px-3 py-2 text-right align-middle tabular-nums ${
        leads ? "font-semibold" : "font-medium text-slate-600"
      }`}
      style={leads ? winnerCell : undefined}
    >
      <span className="inline-flex items-center justify-end gap-1.5">
        {leads && <ArrowUp className="h-3 w-3 shrink-0" aria-hidden="true" />}
        {text}
      </span>
    </td>
  );

  return (
    <tr className="border-b last:border-b-0">
      <th scope="row" className="px-3 py-2 text-left font-normal">
        <span className="block text-xs font-medium text-slate-700">{metric.label}</span>
        {metric.hint && (
          <span className="block text-[10px] leading-tight text-muted-foreground">
            {metric.hint}
          </span>
        )}
      </th>

      {cell(aLeads, aText)}
      {cell(bLeads, bText)}

      <td className="whitespace-nowrap px-3 py-2 text-right text-[11px] tabular-nums">
        {tie ? (
          <span className="inline-flex items-center gap-1 text-slate-400">
            <Equal className="h-3 w-3" aria-hidden="true" />
            Tie
          </span>
        ) : (
          <span className="text-slate-500">
            {kind === "money" ? money(absDelta) : count(absDelta)}
            {pct && <span className="ml-1 text-slate-400">{pct}</span>}
          </span>
        )}
      </td>
    </tr>
  );
}

export default function HierarchyCompare({
  a,
  b,
  accent,
  onClose,
}: {
  a: HierarchyTreeNode;
  b: HierarchyTreeNode;
  accent: string;
  onClose: () => void;
}) {
  const metrics = useMemo<Metric[]>(() => {
    const perAgent = (n: HierarchyTreeNode) => ratio(n.totalProduction, n.totalAgents);
    const soldPerAgent = (n: HierarchyTreeNode) => ratio(n.totalSold, n.totalAgents);

    return [
      {
        key: "production",
        label: "Personal production",
        hint: "This agency only — sold + underwriting",
        kind: "money",
        a: a.production,
        b: b.production,
        group: "Production",
      },
      {
        key: "totalProduction",
        label: "Hierarchy production",
        hint: "Agency plus every agency beneath it",
        kind: "money",
        a: a.totalProduction,
        b: b.totalProduction,
      },
      {
        key: "soldPremium",
        label: "Sold premium",
        hint: "Personal · 'Sold & Approved'",
        kind: "money",
        a: a.soldPremium,
        b: b.soldPremium,
      },
      {
        key: "totalSold",
        label: "Sold premium (hierarchy)",
        kind: "money",
        a: a.totalSold,
        b: b.totalSold,
      },
      {
        key: "underwritingPremium",
        label: "Underwriting premium",
        hint: "Personal · still in flight",
        kind: "money",
        a: a.underwritingPremium,
        b: b.underwritingPremium,
      },
      {
        key: "agentCount",
        label: "Direct agents",
        kind: "count",
        a: a.agentCount,
        b: b.agentCount,
        group: "People",
      },
      {
        key: "totalAgents",
        label: "Total agents",
        hint: "Including every downline agency",
        kind: "count",
        a: a.totalAgents,
        b: b.totalAgents,
      },
      {
        key: "descendantAgencies",
        label: "Sub-agencies beneath",
        kind: "count",
        a: a.descendantAgencies,
        b: b.descendantAgencies,
      },
      {
        key: "soldPolicies",
        label: "Policies sold",
        kind: "count",
        a: a.soldPolicies,
        b: b.soldPolicies,
        group: "Policies",
      },
      {
        key: "underwritingPolicies",
        label: "Policies in underwriting",
        kind: "count",
        a: a.underwritingPolicies,
        b: b.underwritingPolicies,
      },
      {
        key: "perAgent",
        label: "Production per agent",
        // Hierarchy scope on both sides of the division: dividing a branch's rolled-up
        // production by its DIRECT headcount would flatter every manager with a big
        // downline and no personal book.
        hint: "Hierarchy production ÷ total agents",
        kind: "money",
        a: perAgent(a),
        b: perAgent(b),
        group: "Efficiency",
      },
      {
        key: "soldPerAgent",
        label: "Sold premium per agent",
        kind: "money",
        a: soldPerAgent(a),
        b: soldPerAgent(b),
      },
    ];
  }, [a, b]);

  // Headline tally so the answer to "which branch is stronger" is legible before the
  // table is read. Ties count for neither side.
  const tally = useMemo(() => {
    let aWins = 0;
    let bWins = 0;
    for (const m of metrics) {
      const fmt = m.kind === "money" ? money : count;
      if (fmt(m.a) === fmt(m.b)) continue;
      if (m.a > m.b) aWins += 1;
      else bWins += 1;
    }
    return { aWins, bWins };
  }, [metrics]);

  const leaderLabel =
    tally.aWins === tally.bWins
      ? "Evenly matched"
      : tally.aWins > tally.bWins
        ? `${a.name} leads`
        : `${b.name} leads`;

  const branchHead = (node: HierarchyTreeNode, side: "A" | "B") => (
    <th scope="col" className="px-3 py-2 text-right align-bottom">
      <span
        className="mb-0.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide"
        style={{
          color: accent,
          backgroundColor: `color-mix(in oklab, ${accent} 12%, transparent)`,
        }}
      >
        {side}
      </span>
      <span className="block truncate text-xs font-semibold text-slate-900" title={node.name}>
        {node.name}
      </span>
      <span className="block truncate text-[10px] font-normal text-muted-foreground">
        {node.ownerName || "No owner"} · L{node.depth}
      </span>
    </th>
  );

  return (
    <div className="rounded-lg border bg-white">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b px-4 py-2.5">
        <div className="flex min-w-0 items-center gap-2">
          <GitCompare className="h-4 w-4 shrink-0" style={{ color: accent }} aria-hidden="true" />
          <h4 className="truncate text-sm font-semibold text-slate-900">
            {a.name} <span className="font-normal text-slate-400">vs</span> {b.name}
          </h4>
          <span className="shrink-0 text-xs text-muted-foreground">
            {leaderLabel}
            <span className="ml-1 tabular-nums text-slate-400">
              ({tally.aWins}–{tally.bWins})
            </span>
          </span>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close comparison"
          className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Wide on small screens because the branch names are user-supplied and can be
          long — scrolling the table beats truncating the numbers. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[520px] border-collapse text-sm">
          <thead>
            <tr className="border-b bg-slate-50">
              <th
                scope="col"
                className="px-3 py-2 text-left text-[10px] font-semibold uppercase tracking-wide text-slate-500"
              >
                Metric
              </th>
              {branchHead(a, "A")}
              {branchHead(b, "B")}
              <th
                scope="col"
                className="px-3 py-2 text-right align-bottom text-[10px] font-semibold uppercase tracking-wide text-slate-500"
              >
                Gap
              </th>
            </tr>
          </thead>

          <tbody>
            {metrics.map((m) => (
              <React.Fragment key={m.key}>
                {m.group && (
                  <tr className="bg-slate-50/60">
                    <td
                      colSpan={4}
                      className="border-b px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400"
                    >
                      {m.group}
                    </td>
                  </tr>
                )}
                <MetricRow metric={m} accent={accent} />
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      <p className="border-t px-4 py-2 text-[10px] leading-relaxed text-muted-foreground">
        Percentages compare against the smaller side, so &quot;+200%&quot; means three
        times as much. A side with zero shows an absolute gap only.
      </p>
    </div>
  );
}
